import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Menu, Search, Heart, X } from "lucide-react";
import { useWishlist } from "@/context/WishlistContext";
import { useProducts } from "@/context/ProductsContext";
import { BRAND } from "@/data/mockData";
import { motion } from "framer-motion";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/collections", label: "Collections" },
  { to: "/shop", label: "Shop" },
  { to: "/about", label: "About Us" },
  { to: "/custom-orders", label: "Custom Orders" },
  { to: "/corporate-gifting", label: "Corporate Gifting" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [openSheet, setOpenSheet] = useState(false);
  const { count } = useWishlist();
  const { products } = useProducts();
  const navigate = useNavigate();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 12);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const results = q.trim().length > 1
    ? products.filter(p => p.name.toLowerCase().includes(q.toLowerCase()) || p.category.includes(q.toLowerCase())).slice(0, 6)
    : [];

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 1.9 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background,box-shadow,border-color] duration-500 border-b ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-[#E5E5E5] shadow-[0_1px_0_rgba(0,0,0,0.02)]"
          : "bg-white/60 backdrop-blur-md border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="h-20 flex items-center justify-between gap-6">
          {/* Left: mobile menu + logo */}
          <div className="flex items-center gap-3">
            <Sheet open={openSheet} onOpenChange={setOpenSheet}>
              <SheetTrigger asChild>
                <button
                  data-testid="nav-mobile-menu-btn"
                  aria-label="Open menu"
                  className="lg:hidden p-2 -ml-2 rounded-full hover:bg-black/5 transition-colors"
                >
                  <Menu strokeWidth={1.5} className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[85vw] sm:w-[380px] bg-white">
                <SheetHeader className="text-left">
                  <SheetTitle className="flex items-center gap-3">
                    <img src={BRAND.logo} alt="SIRIJA" className="h-8 w-8 rounded-full ring-1 ring-[#E5E5E5]" />
                    <span className="font-serif text-3xl tracking-tight">SIRIJA</span>
                  </SheetTitle>
                </SheetHeader>
                <nav className="mt-8 flex flex-col">
                  {NAV.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => setOpenSheet(false)}
                      data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) =>
                        `py-4 text-lg font-serif border-b border-[#F0F0F0] tracking-tight ${
                          isActive ? "text-[#1A1A1A]" : "text-[#2E2E2E]/80"
                        }`
                      }
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </nav>
                <div className="mt-8 text-xs text-[#A0A0A0] leading-relaxed">
                  {BRAND.address}<br />
                  {BRAND.phone}
                </div>
              </SheetContent>
            </Sheet>

            <Link
              to="/"
              data-testid="nav-logo"
              className="group flex items-center gap-3"
            >
              <img
                src={BRAND.logo}
                alt="SIRIJA logo"
                className="h-9 w-9 rounded-full ring-1 ring-[#E5E5E5] group-hover:ring-[#C0C0C0] transition-[box-shadow,transform] group-hover:rotate-[8deg]"
              />
              <span className="hidden sm:block font-serif text-[24px] tracking-[0.18em] text-[#1A1A1A] leading-none">
                SIRIJA
              </span>
            </Link>
          </div>

          {/* Center: desktop nav with animated underline */}
          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `group relative text-[13px] tracking-wide transition-colors ${
                    isActive ? "text-[#1A1A1A]" : "text-[#2E2E2E]/70 hover:text-[#1A1A1A]"
                  }`
                }
              >
                {({ isActive }) => (
                  <span className="relative pb-1">
                    {item.label}
                    <span
                      className={`absolute left-0 right-0 -bottom-0.5 h-px origin-left transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] bg-[#1A1A1A] ${
                        isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                      }`}
                    />
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Right: search + wishlist */}
          <div className="flex items-center gap-1">
            <button
              data-testid="nav-search-btn"
              aria-label="Search"
              onClick={() => setSearchOpen((v) => !v)}
              className="p-2 rounded-full hover:bg-black/5 transition-colors"
            >
              <Search strokeWidth={1.5} className="h-5 w-5" />
            </button>
            <Link
              to="/wishlist"
              data-testid="nav-wishlist-btn"
              aria-label="Wishlist"
              className="relative p-2 rounded-full hover:bg-black/5 transition-colors"
            >
              <Heart strokeWidth={1.5} className="h-5 w-5" />
              {count > 0 && (
                <span
                  data-testid="wishlist-badge"
                  className="absolute -top-0.5 -right-0.5 text-[10px] bg-[#1A1A1A] text-white rounded-full h-4 min-w-4 px-1 flex items-center justify-center"
                >{count}</span>
              )}
            </Link>
          </div>
        </div>

        {searchOpen && (
          <div data-testid="nav-search-panel" className="pb-5 -mt-2">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A0A0A0]" strokeWidth={1.5} />
              <Input
                data-testid="nav-search-input"
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search rings, mangalsutra, silver coins…"
                className="pl-11 pr-11 h-12 rounded-full border-[#E5E5E5] font-sans"
              />
              <button
                aria-label="Close search"
                onClick={() => { setSearchOpen(false); setQ(""); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-black/5"
              >
                <X className="h-4 w-4" strokeWidth={1.5} />
              </button>
              {results.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-2 bg-white border border-[#E5E5E5] rounded-2xl shadow-lg overflow-hidden">
                  {results.map((r) => (
                    <button
                      key={r.id}
                      data-testid={`search-result-${r.id}`}
                      onClick={() => { setSearchOpen(false); setQ(""); navigate("/shop"); }}
                      className="w-full flex items-center gap-4 px-4 py-3 hover:bg-[#F8F9FA] text-left transition-colors"
                    >
                      <img src={r.image} alt="" className="h-12 w-12 object-cover rounded-md" />
                      <div className="flex-1">
                        <div className="text-sm font-medium">{r.name}</div>
                        <div className="text-xs text-[#A0A0A0]">₹ {r.price.toLocaleString("en-IN")}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </motion.header>
  );
}
